import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Circle, Download, RefreshCw, Terminal, Loader2, ToggleLeft, ToggleRight, Cpu, Zap, Copy, Info, Settings, RotateCcw } from 'lucide-react';
import { apiClient } from '../api/client';
import { toast } from '../utils/toastUtils';
import { useI18n } from '../i18n';

const ToolsList = () => {
  const { t } = useI18n();
  const [tools, setTools] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingTool, setTogglingTool] = useState(null);
  const [bulkToggling, setBulkToggling] = useState(false);
  const [installModal, setInstallModal] = useState({ open: false, tool: null, toolId: null, command: '' });
  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState(null);
  const [stats, setStats] = useState({ total: 0, installed: 0, enabled_for_ai: 0 });
  const [copiedCommand, setCopiedCommand] = useState(null);
  // Config modal state for GPU/threads
  const [configModal, setConfigModal] = useState({ open: false, tool: null, toolId: null });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configData, setConfigData] = useState({ use_gpu: false, threads: 1 });
  const [configDefaults, setConfigDefaults] = useState({ use_gpu: false, threads: 1 });

  // Thread scaling colors
  const threadScalingColors = {
    excellent: 'bg-green-500',
    good: 'bg-lime-500',
    moderate: 'bg-yellow-500',
    poor: 'bg-orange-500',
    none: 'bg-gray-500',
    gpu_only: 'bg-purple-500',
    gpu_primary: 'bg-violet-500'
  };

  const threadScalingLabels = {
    excellent: 'Excellent',
    good: 'Bon',
    moderate: 'Modéré',
    poor: 'Faible',
    none: 'Mono',
    gpu_only: 'GPU Only',
    gpu_primary: 'GPU Primary'
  };

  // Category display names - use i18n translations
  const getCategoryName = (category) => {
    return t(`toolCategories.${category}`, category);
  };

  // Fetch tools from API using axios client
  const fetchTools = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/system/tools');
      const data = response.data;

      setTools(data.tools || {});
      setCategories(data.categories || {});
      setStats({
        total: data.total || 0,
        installed: data.installed || 0,
        enabled_for_ai: data.enabled_for_ai || 0
      });
    } catch (err) {
      console.error('Error fetching tools:', err);
      setError('Erreur lors du chargement des outils');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  // Toggle tool for AI
  const toggleTool = async (toolId) => {
    try {
      setTogglingTool(toolId);

      const response = await apiClient.post(`/system/tools/${toolId}/toggle`);
      const data = response.data;

      // Update local state
      setTools(prev => ({
        ...prev,
        [toolId]: {
          ...prev[toolId],
          enabled_for_ai: data.enabled_for_ai
        }
      }));

      // Update stats
      setStats(prev => ({
        ...prev,
        enabled_for_ai: data.enabled_for_ai
          ? prev.enabled_for_ai + 1
          : prev.enabled_for_ai - 1
      }));

      // Show success notification
      toast.success(`${tools[toolId]?.name || toolId} ${data.enabled_for_ai ? 'active' : 'desactive'} pour l'IA`);

    } catch (err) {
      console.error('Error toggling tool:', err);
      toast.error(`Erreur lors du changement: ${err.message}`);
    } finally {
      setTogglingTool(null);
    }
  };

  // Bulk toggle all tools
  const bulkToggleTools = async (enableAll) => {
    try {
      setBulkToggling(true);

      const response = await apiClient.post(`/system/tools/bulk-toggle?enable_all=${enableAll}`);
      const data = response.data;

      // Refresh tools list to get updated state
      await fetchTools();

      console.log(`Bulk toggle: ${data.updated_count} tools ${enableAll ? 'enabled' : 'disabled'}`);

    } catch (err) {
      console.error('Error bulk toggling tools:', err);
    } finally {
      setBulkToggling(false);
    }
  };

  // Show install command modal
  const showInstallCommand = async (toolId, toolName) => {
    try {
      setInstallResult(null);

      const response = await apiClient.post(`/system/tools/${toolId}/install`);
      const data = response.data;

      setInstallModal({
        open: true,
        tool: toolName,
        toolId: toolId,
        command: data.install_command
      });

    } catch (err) {
      console.error('Error getting install command:', err);
    }
  };

  // Actually install the tool via backend
  const installToolNow = async () => {
    if (!installModal.toolId) return;

    try {
      setInstalling(true);
      setInstallResult(null);

      const response = await apiClient.post(`/system/tools/${installModal.toolId}/install-now`);
      const data = response.data;

      setInstallResult(data);

      // If installation was successful, refresh tools list
      if (data.success && data.installed) {
        await fetchTools();
      }

    } catch (err) {
      console.error('Error installing tool:', err);
      setInstallResult({ success: false, error: err.message });
    } finally {
      setInstalling(false);
    }
  };

  // Open config modal for GPU/threads
  const openConfigModal = async (toolId, tool) => {
    setConfigModal({ open: true, tool: tool, toolId: toolId });
    setConfigLoading(true);
    try {
      const response = await apiClient.get(`/system/tools/${toolId}/config`);
      const data = response.data;
      setConfigDefaults({
        use_gpu: data.server_defaults?.use_gpu || false,
        threads: data.server_defaults?.threads || 1
      });
      setConfigData({
        use_gpu: data.effective?.use_gpu || data.server_defaults?.use_gpu || false,
        threads: data.effective?.threads || data.server_defaults?.threads || 1
      });
    } catch (err) {
      console.error('Error fetching tool config:', err);
      // Use tool defaults
      setConfigDefaults({
        use_gpu: tool.gpu_support || false,
        threads: tool.threads_recommended || 1
      });
      setConfigData({
        use_gpu: tool.gpu_support || false,
        threads: tool.threads_recommended || 1
      });
    } finally {
      setConfigLoading(false);
    }
  };

  // Save tool configuration
  const saveToolConfig = async () => {
    if (!configModal.toolId) return;
    setConfigSaving(true);
    try {
      await apiClient.post(`/system/tools/${configModal.toolId}/config`, null, {
        params: {
          use_gpu: configData.use_gpu,
          threads: configData.threads
        }
      });
      // Refresh tools list to get updated optimal_command
      await fetchTools();
      setConfigModal({ open: false, tool: null, toolId: null });
    } catch (err) {
      console.error('Error saving tool config:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  // Reset tool configuration to defaults
  const resetToolConfig = async () => {
    if (!configModal.toolId) return;
    setConfigSaving(true);
    try {
      await apiClient.delete(`/system/tools/${configModal.toolId}/config`);
      setConfigData({ ...configDefaults });
      // Refresh tools list
      await fetchTools();
    } catch (err) {
      console.error('Error resetting tool config:', err);
    } finally {
      setConfigSaving(false);
    }
  };

  // Copy command to clipboard (avec fallback pour HTTP non-secure)
  const copyToClipboard = (text, toolId = null) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      // Fallback pour les contextes non-HTTPS
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Impossible de copier', err);
      }
      textArea.remove();
    }
    // Visual feedback for copied command
    if (toolId) {
      setCopiedCommand(toolId);
      setTimeout(() => setCopiedCommand(null), 2000);
    }
  };

  // Get status icon
  const getStatusIcon = (tool) => {
    if (tool.installed) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  // Get status text
  const getStatusText = (tool) => {
    if (tool.installed) {
      return tool.version ? `v${tool.version.split(' ')[0]}` : 'Installé';
    }
    return 'Non installé';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">Chargement des outils...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={fetchTools}
          className="btn-green px-4 py-2 flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Réessayer
        </button>
      </div>
    );
  }

  // Group tools by category (excluding wireless - on ne gère pas le wifi)
  const toolsByCategory = {};
  Object.entries(tools).forEach(([id, tool]) => {
    const cat = tool.category || 'other';
    // Exclure la catégorie wireless
    if (cat === 'wireless') return;
    if (!toolsByCategory[cat]) {
      toolsByCategory[cat] = [];
    }
    toolsByCategory[cat].push({ id, ...tool });
  });

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-2xl font-bold text-brand-green">{stats.installed}</div>
              <div className="text-xs text-gray-400">Installés</div>
            </div>
            <div className="h-8 w-px bg-gray-700"></div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{stats.enabled_for_ai}</div>
              <div className="text-xs text-gray-400">Activés IA</div>
            </div>
            <div className="h-8 w-px bg-gray-700"></div>
            <div>
              <div className="text-2xl font-bold text-gray-400">{stats.total}</div>
              <div className="text-xs text-gray-400">Total</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Bulk Toggle Buttons */}
            <button
              onClick={() => bulkToggleTools(true)}
              disabled={bulkToggling}
              className="flex items-center gap-2 px-3 py-2 bg-brand-green/20 hover:bg-brand-green/30 text-brand-green rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              title="Activer tous les outils installés pour l'IA"
            >
              {bulkToggling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ToggleRight className="w-4 h-4" />
              )}
              Tout activer
            </button>
            <button
              onClick={() => bulkToggleTools(false)}
              disabled={bulkToggling}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              title="Désactiver tous les outils pour l'IA"
            >
              {bulkToggling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              Tout désactiver
            </button>

            <div className="h-8 w-px bg-gray-700 mx-1"></div>

            <button
              onClick={fetchTools}
              className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5 text-gray-400 hover:text-brand-green" />
            </button>
          </div>
        </div>
      </div>

      {/* Tools by Category */}
      {Object.entries(toolsByCategory).map(([category, categoryTools]) => (
        <div key={category} className="bg-brand-black rounded-lg p-5 border border-gray-800">
          <h3 className="font-bold text-brand-green mb-4 flex items-center gap-2">
            {getCategoryName(category)}
            <span className="text-xs text-gray-500 font-normal">
              ({categoryTools.filter(t => t.installed).length}/{categoryTools.length} installés)
            </span>
          </h3>

          <div className="space-y-3">
            {categoryTools.map((tool) => (
              <div
                key={tool.id}
                className={`p-3 rounded transition-colors ${
                  tool.installed
                    ? 'bg-brand-black-lighter hover:bg-brand-black'
                    : 'bg-gray-900/50 opacity-75'
                }`}
              >
                {/* Ligne principale */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="font-semibold text-white">{tool.name}</div>
                      {getStatusIcon(tool)}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        tool.installed
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {getStatusText(tool)}
                      </span>

                      {/* GPU Badge */}
                      {tool.gpu_support ? (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          <Zap className="w-3 h-3" />
                          GPU ON
                          {tool.gpu_vram_gb > 0 && (
                            <span className="text-purple-300 ml-1">{tool.gpu_vram_gb}GB</span>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-600/30 text-gray-400 border border-gray-600/30">
                          <Cpu className="w-3 h-3" />
                          CPU
                        </span>
                      )}

                      {/* Threading Badge */}
                      {tool.threads_max > 1 && (
                        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${
                          tool.thread_scaling === 'excellent' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          tool.thread_scaling === 'good' ? 'bg-lime-500/20 text-lime-400 border-lime-500/30' :
                          tool.thread_scaling === 'moderate' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                          tool.thread_scaling === 'poor' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                          tool.thread_scaling === 'gpu_only' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                          tool.thread_scaling === 'gpu_primary' ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' :
                          'bg-gray-600/20 text-gray-400 border-gray-600/30'
                        }`}>
                          <span className="font-mono">{tool.threads_recommended}/{tool.threads_max}</span>
                          <span className="text-[10px] opacity-75">threads</span>
                          <span className={`w-1.5 h-1.5 rounded-full ${threadScalingColors[tool.thread_scaling] || 'bg-gray-500'}`}></span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{tool.description}</p>
                  </div>

                  <div className="flex items-center gap-3 ml-4">
                    {tool.installed ? (
                      <>
                        {/* Bouton Config GPU/Threads */}
                        <button
                          onClick={() => openConfigModal(tool.id, tool)}
                          className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                          title="Configurer GPU/Threads"
                        >
                          <Settings className="w-4 h-4 text-gray-400 hover:text-brand-green" />
                        </button>
                        <span className="text-xs text-gray-500">
                          {tool.enabled_for_ai ? 'IA activé' : 'IA désactivé'}
                        </span>
                        <button
                          onClick={() => toggleTool(tool.id)}
                          disabled={togglingTool === tool.id}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            tool.enabled_for_ai ? 'bg-brand-green' : 'bg-gray-600'
                          } ${togglingTool === tool.id ? 'opacity-50' : ''}`}
                        >
                          {togglingTool === tool.id ? (
                            <Loader2 className="w-4 h-4 text-white animate-spin mx-auto" />
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                tool.enabled_for_ai ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => showInstallCommand(tool.id, tool.name)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded text-sm transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Installer
                      </button>
                    )}
                  </div>
                </div>

                {/* Commande optimale (si disponible) */}
                {tool.optimal_command && tool.installed && (
                  <div className="mt-2 pt-2 border-t border-gray-800">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Terminal className="w-3 h-3" />
                        <span>Commande optimale:</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(tool.optimal_command, tool.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                          copiedCommand === tool.id
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                        title="Copier la commande"
                      >
                        {copiedCommand === tool.id ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Copié!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copier
                          </>
                        )}
                      </button>
                    </div>
                    <code className="block mt-1 text-xs font-mono text-brand-green/80 bg-brand-black/50 p-2 rounded overflow-x-auto">
                      {tool.optimal_command}
                    </code>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Info Box */}
      <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-brand-green mt-0.5">💡</div>
          <div className="text-sm text-gray-300">
            <div className="font-medium text-brand-green mb-1">Configuration IA Orchestrateur</div>
            <div>
              Les outils activés (ON) seront utilisés automatiquement par l'IA lors des scans.
              L'orchestrateur décide intelligemment quels outils lancer selon les services détectés.
            </div>
            <div className="mt-2 text-xs text-gray-400">
              Exemple: Port 80 détecté → IA lance automatiquement Nikto + Gobuster + Nuclei si activés
            </div>
          </div>
        </div>
      </div>

      {/* Config GPU/Threads Modal */}
      {configModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-brand-black-light border border-brand-green/30 rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-6 h-6 text-brand-green" />
              <h3 className="text-lg font-bold text-white">Configuration: {configModal.tool?.name}</h3>
            </div>

            {configLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-6">
                  Personnalisez les paramètres GPU et threads pour cet outil.
                  Les valeurs par défaut sont les recommandations serveur.
                </p>

                {/* GPU Toggle */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-400" />
                      Mode GPU
                    </label>
                    <div className="flex items-center gap-2">
                      {!configModal.tool?.gpu_support && (
                        <span className="text-xs text-gray-500">Non supporté</span>
                      )}
                      <button
                        onClick={() => setConfigData(prev => ({ ...prev, use_gpu: !prev.use_gpu }))}
                        disabled={!configModal.tool?.gpu_support}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          configData.use_gpu ? 'bg-purple-500' : 'bg-gray-600'
                        } ${!configModal.tool?.gpu_support ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            configData.use_gpu ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {configModal.tool?.gpu_support
                      ? `GPU recommandé avec ${configModal.tool?.gpu_vram_gb || 0}GB VRAM minimum`
                      : 'Cet outil ne supporte pas l\'accélération GPU'
                    }
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Défaut serveur: {configDefaults.use_gpu ? 'GPU activé' : 'CPU uniquement'}
                  </p>
                </div>

                {/* Threads Slider */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white font-medium flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-blue-400" />
                      Nombre de threads
                    </label>
                    <span className="text-brand-green font-mono font-bold text-lg">
                      {configData.threads}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max={configModal.tool?.threads_max || 16}
                    value={configData.threads}
                    onChange={(e) => setConfigData(prev => ({ ...prev, threads: parseInt(e.target.value) }))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-brand-green"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>Max: {configModal.tool?.threads_max || 16}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Défaut serveur: {configDefaults.threads} threads
                    (scaling: {configModal.tool?.thread_scaling || 'N/A'})
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex justify-between gap-3">
                  <button
                    onClick={resetToolConfig}
                    disabled={configSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Réinitialiser
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfigModal({ open: false, tool: null, toolId: null })}
                      disabled={configSaving}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded transition-colors disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={saveToolConfig}
                      disabled={configSaving}
                      className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green/80 text-black font-medium rounded transition-colors disabled:opacity-50"
                    >
                      {configSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        'Enregistrer'
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Install Command Modal */}
      {installModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-brand-black-light border border-brand-green/30 rounded-xl p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Terminal className="w-6 h-6 text-brand-green" />
              <h3 className="text-lg font-bold text-white">Installer {installModal.tool}</h3>
            </div>

            {!installResult ? (
              <>
                <p className="text-gray-400 text-sm mb-4">
                  Installez automatiquement ou copiez la commande pour l'exécuter manuellement:
                </p>

                <div className="bg-brand-black rounded-lg p-4 font-mono text-sm text-green-400 mb-4 relative">
                  <code className="break-all">{installModal.command}</code>
                  <button
                    onClick={() => copyToClipboard(installModal.command)}
                    className="absolute top-2 right-2 p-2 hover:bg-brand-black-lighter rounded text-gray-400 hover:text-white transition-colors"
                    title="Copier"
                  >
                    📋
                  </button>
                </div>

                <div className="flex justify-between gap-3">
                  <button
                    onClick={installToolNow}
                    disabled={installing}
                    className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {installing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Installation en cours...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Installer maintenant
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      copyToClipboard(installModal.command);
                    }}
                    disabled={installing}
                    className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
                  >
                    Copier
                  </button>
                  <button
                    onClick={() => {
                      setInstallModal({ open: false, tool: null, toolId: null, command: '' });
                      setInstallResult(null);
                    }}
                    disabled={installing}
                    className="px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded transition-colors disabled:opacity-50"
                  >
                    Fermer
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Installation Result */}
                <div className={`rounded-lg p-4 mb-4 ${
                  installResult.success && installResult.installed
                    ? 'bg-green-500/20 border border-green-500/30'
                    : 'bg-red-500/20 border border-red-500/30'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {installResult.success && installResult.installed ? (
                      <>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                        <span className="text-green-400 font-medium">Installation réussie!</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-6 h-6 text-red-500" />
                        <span className="text-red-400 font-medium">Échec de l'installation</span>
                      </>
                    )}
                  </div>
                  {installResult.error && (
                    <p className="text-red-300 text-sm">{installResult.error}</p>
                  )}
                </div>

                {/* Output logs */}
                {(installResult.stdout || installResult.stderr) && (
                  <div className="mb-4">
                    <p className="text-gray-400 text-sm mb-2">Sortie de l'installation:</p>
                    <div className="bg-brand-black rounded-lg p-3 max-h-48 overflow-y-auto">
                      {installResult.stdout && (
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">{installResult.stdout}</pre>
                      )}
                      {installResult.stderr && (
                        <pre className="text-xs text-red-400 whitespace-pre-wrap font-mono mt-2">{installResult.stderr}</pre>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  {!installResult.success && (
                    <button
                      onClick={() => setInstallResult(null)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                      Réessayer
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setInstallModal({ open: false, tool: null, toolId: null, command: '' });
                      setInstallResult(null);
                    }}
                    className="px-4 py-2 bg-brand-green hover:bg-brand-green/80 text-black font-medium rounded transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsList;
